import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import HeroSlider from "@/components/site/HeroSlider";
import BookCard from "@/components/site/BookCard";
import { useI18n } from "@/lib/i18n";
import SEO from "@/components/SEO";
import { ArrowRight, Trophy, FileText } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";
import { FacebookIcon } from "react-share";

export default function Home () {
    const { t, lang } = useI18n();
    const [featured, setFeatured] = useState([]);
    const [ebooks, setEbooks] = useState([]);
    const [mcq, setMcq] = useState([]);
    const [winners, setWinners] = useState([]);
    const [blogs, setBlogs] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/books?category=written&featured=true"),
            api.get("/books?category=ebook&featured=true"),
            api.get("/books?category=mcq&featured=true"),
            api.get("/winners"),
            api.get("/blogs?limit=4"),
        ])
            .then(([featuredRes, ebooksRes, mcqRes, winnersRes, blogsRes]) => {
                const sortByOrder = (arr) =>
                    [...arr].sort((a, b) => (a.order || 0) - (b.order || 0));

                setFeatured(sortByOrder(featuredRes.data));
                setEbooks(sortByOrder(ebooksRes.data));
                setMcq(sortByOrder(mcqRes.data));
                setWinners(winnersRes.data);
                setBlogs(sortByOrder(blogsRes.data));
            })
            .catch(() => { });
    }, []);

    return (
        <div>
            <SEO title={lang === "bn" ? "হোম" : "Home"} />
            <HeroSlider />

            {/* Featured Books */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-8 lg:py-12">
                <div className="flex items-end justify-between pb-4 md:pb-6 lg:mb-8">
                    <div>
                        <div className="text-xs font-medium tracking-wider text-brand-red uppercase">{t("site_name")}</div>
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brand-navy mt-1">{t("featured")}</h2>
                    </div>
                    <Link to="/books/written" className="text-brand-royal hover:text-brand-red font-medium text-sm hidden sm:inline-flex items-center gap-1">
                        {lang === "bn" ? "সকল বই" : "All books"} <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-5">
                    {featured.map((b) => <BookCard key={b.id} book={b} />)}
                </div>
            </section>

            {/* E-Book Collection */}
            {ebooks.length > 0 && (
                <section className="bg-gradient-to-br from-[#08145A] via-[#0B1E8A] to-[#08145A] relative overflow-hidden">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-8 lg:py-12 relative">
                        <div className="flex items-end justify-between pb-4 md:pb-6 lg:mb-8 flex-wrap gap-3">
                            <div>
                                <div className="text-xs font-medium tracking-wider text-[#AFC4D6] inline-flex items-center gap-2">
                                    <FileText className="w-3.5 h-3.5" /> DIGITAL E-BOOKS
                                </div>
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mt-1">{lang === "bn" ? "ই-বুক কালেকশন" : "E-Book Collection"}</h2>
                                
                            </div>
                            <Link to="/books/ebook" className="text-white hover:text-[#AFC4D6] font-medium text-sm inline-flex items-center gap-1">
                                {lang === "bn" ? "সব ই-বুক" : "All e-books"} <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-5">
                            {ebooks.slice(0, 4).map((b) => <BookCard key={b.id} book={b} />)}
                        </div>
                    </div>
                </section>
            )}

            {/* MCQ Collection */}
            {mcq.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-8 lg:py-12">
                    <div className="flex items-end justify-between pb-4 md:pb-6 lg:mb-8">
                        <div>
                            <div className="text-xs font-medium tracking-wider text-brand-red uppercase">{t("site_name")}</div>
                            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brand-navy mt-1">{t("nav_mcq")}</h2>
                        </div>
                        <Link to="/books/mcq" className="text-brand-royal hover:text-brand-red font-medium text-sm hidden sm:inline-flex items-center gap-1">
                            {lang === "bn" ? "সকল বই" : "All books"} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-5">
                        {mcq.map((b) => <BookCard key={b.id} book={b} />)}
                    </div>
                </section>
            )}

            {/* Winners section */}
            <section className="bg-hero-grad relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-8 lg:py-12 relative">

                    <div className="text-center max-w-2xl mx-auto pb-4 md:pb-6 lg:mb-8">
                        <Trophy className="w-10 h-10 mx-auto text-brand-red" />
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brand-navy mt-3">
                            {t("winners_title")}
                        </h2>
                        <p className="text-brand-navy/65 mt-2">{t("winners_sub")}</p>
                    </div>

                    <Swiper
                        modules={[Autoplay]}
                        autoplay={{
                            delay: 3500,
                            disableOnInteraction: false,
                        }}
                        loop={true}
                        spaceBetween={20}
                        breakpoints={{
                            0: {
                                slidesPerView: 1,
                            },
                            768: {
                                slidesPerView: 2,
                            },
                            1024: {
                                slidesPerView: 3,
                            },
                        }}
                    >
                        {winners?.map((w) => (
                            <SwiperSlide key={w.id}>
                                <div className="glass rounded-2xl p-6 h-full">
                                    <div className="mt-5 flex flex-row items-center justify-between gap-3 w-full">
                                        <div className="flex items-center gap-3">
                                            <img
                                                src={w.image}
                                                alt={w.name}
                                                className="w-16 h-16 rounded-full object-cover"
                                            />
                                            <div>
                                                <div className="font-semibold text-brand-navy">
                                                    {w.name}
                                                </div>
                                                <div className="text-xs text-brand-royal/80">
                                                    {w.designation}
                                                </div>
                                                <div className="text-[11px] text-brand-navy/55">
                                                    {w.department}
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <Link className="rounded-full" to={w.social_url} target="_blank" rel="noopener noreferrer">
                                                <FacebookIcon className="w-5 h-5 text-brand-red rounded-full" />
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                </div>
            </section>

            {/* Blog section */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-8 lg:py-12">

                {/* BLOG SECTION */}
                <div className="flex flex-col flex-wrao w-full">
                    <div className="flex items-center justify-between pb-4 md:pb-6 lg:mb-8">
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-brand-navy">
                            {t("blog_title")}
                        </h3>
                        <Link
                            to="/blog"
                            className="text-brand-royal hover:text-brand-red text-xs sm:text-sm font-medium whitespace-nowrap flex items-center gap-1.5"
                        >
                            {lang === "bn" ? "সব ব্লগ" : "All blogs"} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-5">
                        {blogs.map((b) => (
                            <Link
                                key={b.id}
                                to={`/blog/${ b.id }`}
                                className="flex flex-col flex-wrap gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border border-brand-light hover:shadow-soft hover:-translate-y-0.5 transition"
                            >
                                <img
                                    src={b.cover_image}
                                    alt={b.title}
                                    className="w-full aspect-video object-cover rounded-lg sm:rounded-xl flex-shrink-0"
                                />

                                <div className="min-w-0">
                                    <div className="text-[10px] uppercase tracking-wider text-brand-red font-bold">
                                        {b.category}
                                    </div>

                                    <h4 className="font-semibold text-sm sm:text-base text-brand-navy line-clamp-2 leading-tight mt-1">
                                        {b.title}
                                    </h4>

                                    <div className="text-xs text-brand-navy/55 mt-2">
                                        👁 {b.views} • {b.author}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>

            </section>

        </div>
    );
}
